import cn from "classnames";
import { FunctionComponent, ReactElement, ReactNode, useRef, useState, useMemo } from "react";
import { IconType } from "react-icons";
import { NavLink, NavLinkProps, To } from "react-router-dom";
import { useMedia } from "react-use";
import { HoverTooltip } from "../shared/HoverTooltip";
import { CustomClaimValue, CustomClaimValueDisplayNames, useAuth } from "@app/shared";
import { toastWarning } from "../../helpers";
import { useHasClaim } from "../../hooks";

type SidebarIconProps = Omit<NavLinkProps, "className" | "to"> & {
    Icon?: IconType;
    to?: To;
    className?: string;
    claim?: CustomClaimValue;
    hover: string | ReactElement;
};

export const SidebarIcon: FunctionComponent<SidebarIconProps> = (props) => {
    const { Icon, claim, to, children, hover, ...rest } = props;
    const [showTooltip, setShowTooltip] = useState(false);
    const popperButtonRef = useRef(null);
    const auth = useAuth();
    // Below md the rail is a drawer with room for the name beside each icon, so the name is
    // printed and the hover tooltip (which a tap would also trigger) stays off.
    const isRail = useMedia("(min-width: 768px)");

    const hasClaim = useHasClaim(claim!, auth.currentOrganizationId, undefined);
    const hasPermission = useMemo(() => {
        if (!claim) {
            return true;
        }
        return !!hasClaim;
    }, [claim, hasClaim]);

    const onClick = () => {
        if (claim && !hasPermission) {
            toastWarning("Permission Error", `${CustomClaimValueDisplayNames[claim]} claim is required.`);
        }
    };

    const label = <span className="md:hidden flex-1 min-w-0 truncate pl-3 text-base font-semibold">{hover}</span>;

    const wrappedChildren = Icon ? (
        <>
            <div className="w-2"></div>
            <div className="md:flex-1 flex items-center justify-center pl-2 md:pl-0">
                <Icon className="w-9 h-9" />
            </div>
            {label}
            <div className="w-2 flex flex-col justify-center">
                <div className="nav-triangle" />
            </div>
        </>
    ) : (
        <>
            <div className="w-2"></div>
            <div className="md:flex-1 flex items-center justify-center pl-2 md:pl-0">{children as ReactNode}</div>
            {label}
            <div className="w-2 flex flex-col justify-center">
                <div className="nav-triangle" />
            </div>
        </>
    );

    const HoverNode =
        to && hasPermission ? (
            <NavLink to={to} {...rest} className={({ isActive }) => cn("w-full flex flex-grow items-center", { "text-link-left-nav-active nav-active": isActive })}>
                {wrappedChildren}
            </NavLink>
        ) : hasPermission ? (
            <div className="w-full flex flex-grow items-center">{wrappedChildren}</div>
        ) : (
            <div className="w-full flex flex-grow items-center text-[#FFFFFF] text-opacity-25 cursor-not-allowed" onClick={onClick}>
                {wrappedChildren}
            </div>
        );

    return (
        <HoverTooltip
            popperButtonRef={popperButtonRef}
            show={showTooltip && isRail}
            hoverable={
                <li ref={popperButtonRef} className={cn("w-full text-link-left-nav flex place-content-end ", { "hover:text-link-left-nav-hover": hasPermission })} onMouseOver={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
                    {HoverNode}
                </li>
            }
            placement="right"
            offset={[-27, 0]}
            className="h-8 bg-background-left-nav rounded-2xl text-sm font-semibold border border-bright-text text-bright-text px-3 py-0 flex items-center"
        >
            {hover}
        </HoverTooltip>
    );
};
